import { useCurrency } from '@blend-ed/blendx-platform/hooks';
import { Badge, Button, ContentDivider, Icon, Image, ShareButtonComponent } from '@blend-ed/blendx-ui';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import React from 'react';
import placeholderDark from '../assets/images/placeholder-dark.png';
import placeholder from '../assets/images/placeholder.jpg';
import messages from './messages';

// Helper component to render badges with icons and labels
function CourseExploreCardBadge({ label, icon }) {
    return (
        <div className='course-discover-card__badge'>
            {label && <Icon icon={icon} type="line" size="sm" className="course-discover-card__badge-icon" />}
            {label && <span className="course-discover-card__label">{label}</span>}
        </div>
    )
}

function CourseExploreCard(props) {
    // Props destructuring - supports both course and program scenarios
    const {
        preview,
        price,
        estimatedTime,
        author,
        cardType,
        courseCount,
        primaryBtnText,
        primaryOnClick,
        secondaryBtnText,
        secondaryOnClick,
        inviteOnly,
        alreadyEnrolled,
        achievements,
        onDiscoverPage,
        published,
        startDate,
        intl,
        programTags,
        title
    } = props
    const { symbol } = useCurrency();

    // Date formatting logic for start date display
    const isStartDateInFuture = startDate && new Date(startDate) > new Date();
    const formattedStartDate = startDate ? new Date(startDate).toLocaleDateString() : '';
    const startDateBadgeLabel = isStartDateInFuture ? intl.formatMessage(messages.startDateInFuture, { startDate: formattedStartDate }) : intl.formatMessage(messages.started, { startDate: formattedStartDate });
    const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';

    return (
        <div className='course-discover-card__wrapper'>
            {/* Invite-only badge - applies to both courses and programs */}
            {inviteOnly && <Badge variant="success" className='course-discover-card__invite-only-badge'>{intl.formatMessage(messages.inviteOnly)}</Badge>}

            {/* Preview image with fallback - common for both courses and programs */}
            <Image src={preview} alt="" className='course-discover-card__preview-img' onError={(e) => {
                e.target.src = isDarkMode ? placeholderDark : placeholder;
            }} />

            <div className="course-discover-card__title">
                {title}
            </div>

            {/* Price section - displayed only if not already enrolled */}
            {!alreadyEnrolled && <div className="course-discover-card__price-section">
                <span className={`course-discover-card__price ${price === 0 ? 'free' : ''}`}>
                    {price === 0 ?
                        intl.formatMessage(messages.free) :
                        (<>
                            <span className="currency-symbol">{symbol}</span>
                            {price}
                        </>)
                    }
                </span>
                <div className="course-discover-card__share-icon">
                    <ShareButtonComponent url={window.location.href} />
                </div>
            </div>}

            {/* Info section with badges - displays different info based on cardType */}
            <div className="course-discover-card__info-section">
                {/* For programs: Shows course count */}
                {cardType === 'program' && courseCount && <CourseExploreCardBadge icon="book" label={courseCount} />}

                {/* For both: Shows estimated time*/}
                {estimatedTime && <CourseExploreCardBadge icon="time" label={estimatedTime} />}

                {/* For both: Shows start date if available */}
                {startDate && <CourseExploreCardBadge icon="calendar" label={startDateBadgeLabel} />}

                {/* For both: Shows author if available */}
                {author && <CourseExploreCardBadge icon="user-2" label={author} />}
            </div>

            {/* Action buttons - shown only if not enrolled and course/program is published */}
            {!alreadyEnrolled && published &&
                <div className="course-discover-card__btns-section">
                    <Button size="md" variant="primary" onClick={primaryOnClick} className="course-discover-card__btns" disabled={inviteOnly}>{primaryBtnText}</Button>
                    <Button size="md" variant="outline-gray" className="course-discover-card__btns" onClick={secondaryOnClick}>{secondaryBtnText}</Button>
                </div>}

            {/* Already enrolled indicator - shown on discover page if user is enrolled */}
            {alreadyEnrolled && onDiscoverPage &&
                <div className="course-discover-card__already-enrolled-section">
                    <div className="course-discover-card__status-badge course-discover-card__status-badge--enrolled">
                        <Icon icon="checkbox-circle" type="line" className="status-icon" size="sm" />
                        {intl.formatMessage(messages.alreadyEnrolled)}
                    </div>
                </div>}

            {/* Unpublished indicator - shown on discover page if course/program is not yet published */}
            {!published && onDiscoverPage &&
                <div className="course-discover-card__already-enrolled-section">
                    <div className="course-discover-card__status-badge course-discover-card__status-badge--unpublished">
                        <Icon icon="time" type="line" className="status-icon" size="sm" />
                        {intl.formatMessage(messages.notAvailable)}
                    </div>
                </div>}

            {/* Program-specific: Achievements/outcomes section */}
            {achievements && achievements.length > 0 && achievements?.[0] !== '' && <div className="course-discover-card__program-outcome-section">
                <ContentDivider />
                <span className="course-discover-card__program-outcome-title">{intl.formatMessage(messages.whatCanBeAchieved)}</span>
                <ol className="course-discover-card__program-outcome-list">
                    {achievements.map((achievement, index) => (
                        <li className="course-discover-card__program-outcome-item" key={index}> {achievement}</li>
                    ))}
                </ol>
            </div>}

            {/* Program-specific: Tags section */}
            {
                programTags && programTags.length > 0 && <div className="course-discover-card__program-tags-section">
                    <ContentDivider />
                    <span className="course-discover-card__program-tags-title">{intl.formatMessage(messages.tags)}</span>
                    <div className="course-discover-card__program-tags-list">
                        {programTags.map(tagObject => <Badge className="badge-outline-gray" variant="disabled">{tagObject.tag}</Badge>)}
                    </div>
                </div>
            }

        </div>
    )
}

CourseExploreCard.propTypes = {
    intl: intlShape.isRequired,
};

export default injectIntl(CourseExploreCard);